import React from 'react';
import styled from 'styled-components';

const NewspaperContainer = styled.div`
  background: linear-gradient(to bottom, #f4f1e8 0%, #e9e4d4 100%);
  border: 3px solid #1a1a1a;
  box-shadow: 
    0 6px 16px rgba(0,0,0,0.2),
    inset 0 0 120px rgba(0,0,0,0.03);
  padding: 0;
  margin: 48px auto;
  max-width: 1400px;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: 
      repeating-linear-gradient(
        0deg,
        transparent,
        transparent 2px,
        rgba(0,0,0,0.015) 2px,
        rgba(0,0,0,0.015) 4px
      );
    pointer-events: none;
  }

  @media (max-width: 768px) {
    margin: 24px auto;
    border-width: 2px;
  }
`;

const NewspaperHeader = styled.div`
  background: linear-gradient(to bottom, #ebe8dc 0%, #e3dfd0 100%);
  border-bottom: 3px solid #1a1a1a;
  padding: 20px 40px;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -3px;
    left: 0;
    right: 0;
    height: 1px;
    background: rgba(0,0,0,0.15);
  }

  @media (max-width: 768px) {
    padding: 16px 20px;
    border-bottom-width: 2px;
  }
`;

const MastheadTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
  padding-bottom: 8px;
  border-bottom: 2px double rgba(0,0,0,0.3);
`;

const MastheadLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const MastheadName = styled.div`
  font-family: 'Georgia', 'Times New Roman', serif;
  font-size: 1.1rem;
  font-weight: 700;
  letter-spacing: 1px;
  color: #1a1a1a;
  text-transform: uppercase;

  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
`;

const EstablishedText = styled.div`
  font-family: 'Georgia', 'Times New Roman', serif;
  font-size: 0.6rem;
  font-style: italic;
  color: #3a3a3a;
  letter-spacing: 0.3px;

  @media (max-width: 768px) {
    font-size: 0.55rem;
  }
`;

const PriceInfo = styled.div`
  font-family: 'Georgia', 'Times New Roman', serif;
  font-size: 0.7rem;
  font-weight: 600;
  color: #2a2a2a;
  text-align: right;
  line-height: 1.4;

  @media (max-width: 768px) {
    font-size: 0.6rem;
  }
`;

const WeatherInfo = styled.div`
  font-family: 'Georgia', 'Times New Roman', serif;
  font-size: 0.65rem;
  color: #3a3a3a;
  text-align: center;
  margin-top: 4px;
  font-style: italic;
  letter-spacing: 0.3px;

  @media (max-width: 768px) {
    font-size: 0.6rem;
  }
`;

const TitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  gap: 20px;

  @media (max-width: 768px) {
    gap: 10px;
    margin-bottom: 12px;
  }
`;

const SpecialLabel = styled.div`
  border: 2px solid #1a1a1a;
  padding: 4px 10px;
  background: rgba(255,255,255,0.6);
  font-family: 'Georgia', 'Times New Roman', serif;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #1a1a1a;

  @media (max-width: 768px) {
    font-size: 0.65rem;
  }
`;

const DateLabel = styled.div`
  border: 2px solid #1a1a1a;
  padding: 4px 10px;
  background: rgba(255,255,255,0.6);
  font-family: 'Georgia', 'Times New Roman', serif;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #1a1a1a;

  @media (max-width: 768px) {
    font-size: 0.65rem;
  }
`;

const MainHeadline = styled.h1`
  font-family: 'Georgia', 'Times New Roman', serif;
  font-size: 3.5rem;
  font-weight: 900;
  text-align: center;
  margin: 0;
  color: #000;
  letter-spacing: -1px;
  line-height: 1.1;
  text-transform: capitalize;
  text-shadow: 1px 1px 0px rgba(0,0,0,0.1);
  flex: 1;

  @media (max-width: 1024px) {
    font-size: 2.8rem;
  }

  @media (max-width: 768px) {
    font-size: 1.8rem;
    letter-spacing: -0.5px;
  }
`;

const SubHeadline = styled.div`
  font-family: 'Georgia', 'Times New Roman', serif;
  font-size: 0.85rem;
  text-align: center;
  margin-top: 8px;
  color: #2a2a2a;
  font-style: italic;
  letter-spacing: 0.3px;

  @media (max-width: 768px) {
    font-size: 0.75rem;
  }
`;

const VolumeInfo = styled.div`
  font-family: 'Georgia', 'Times New Roman', serif;
  font-size: 0.7rem;
  text-align: center;
  margin-top: 6px;
  margin-bottom: 12px;
  padding: 8px 0;
  color: #3a3a3a;
  font-weight: 600;
  letter-spacing: 0.5px;
  border-top: 1px solid rgba(0,0,0,0.2);
  border-bottom: 1px solid rgba(0,0,0,0.2);
  position: relative;

  &::before,
  &::after {
    content: '❖';
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    font-size: 0.6rem;
    color: #2a2a2a;
  }

  &::before {
    left: 20px;
  }

  &::after {
    right: 20px;
  }

  @media (max-width: 768px) {
    font-size: 0.65rem;
    
    &::before,
    &::after {
      font-size: 0.5rem;
    }

    &::before {
      left: 10px;
    }

    &::after {
      right: 10px;
    }
  }
`;

const ContentArea = styled.div`
  padding: 32px 40px 40px;
  background: rgba(255, 255, 255, 0.3);

  @media (max-width: 768px) {
    padding: 20px 16px 24px;
  }
`;

const NewspaperWrapper = ({ 
  title = "Sprezzatura Collection", 
  subtitle = "A Movement Strikes the Streets",
  volume = "VOL. I, No. 1",
  date = new Date().toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' }).toUpperCase(),
  children 
}) => {
  return (
    <NewspaperContainer>
      <NewspaperHeader>
        <TitleRow>
          <SpecialLabel>LATEST SPECIAL</SpecialLabel>
          <MainHeadline>{title}</MainHeadline>
          <DateLabel>DAILY REPORT</DateLabel>
        </TitleRow>
        <MastheadTop>
          <MastheadLeft>
            <MastheadName>The Sprezzatura Monthly</MastheadName>
            <EstablishedText>Established 2024 • New York City</EstablishedText>
          </MastheadLeft>
          <PriceInfo>
            <div>PRICE: TWO CENTS</div>
            <div>EVENING EDITION</div>
            <WeatherInfo>Fair & Mild</WeatherInfo>
          </PriceInfo>
        </MastheadTop>
        <VolumeInfo>{volume} — A Movement strikes the streets of New York, soon NYC — {date}</VolumeInfo>
        <SubHeadline>{subtitle}</SubHeadline>
      </NewspaperHeader>
      <ContentArea>
        {children}
      </ContentArea>
    </NewspaperContainer>
  );
};

export default NewspaperWrapper;
